import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

import { nativeRecorderAvailable, nativeRecorderStatus } from '../../lib/native-recorder'

// ============================================================================
// THE DAY SCREEN'S LIVE READS — the browser's own facts (online, the phone's recorder) and the
// two measurements the hero needs to fit any width. Each writes as little React state as it can.
// ============================================================================

/** `navigator.onLine`, kept current. True where the browser cannot say. */
export function useOnline(): boolean {
  const read = () => (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : true)
  const [online, setOnline] = useState(read)
  useEffect(() => {
    const update = () => setOnline(read())
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])
  return online
}

export interface LiveRecording {
  /** ISO, when the phone reported one. Absent means the length is not known — never "0:00". */
  startedAt: string | null
  courseCode: string | null
}

/** How often the recorder is asked while it is recording (the Recordings screen's own beat). */
const POLL_MS = 1_000

/**
 * IS THE PHONE RECORDING A LECTURE RIGHT NOW? Only the LifeOS Android app can say: the recorder is
 * its native service (lib/native-recorder.ts). In a browser tab this is always null, and the chip
 * never shows a recording it cannot see. One probe on mount and on every return to the foreground
 * (a class alarm can start one while the phone is locked), then a poll only while it records.
 *
 * `fixture` is the demo `recording` state.
 */
export function useLiveRecording(fixture: LiveRecording | null): LiveRecording | null {
  const [live, setLive] = useState<LiveRecording | null>(null)
  const mounted = useRef(true)

  const probe = useCallback(async () => {
    const answer = await nativeRecorderStatus()
    if (!mounted.current) return
    if (answer.available && answer.value.recording) {
      setLive((prev) => {
        const next = { startedAt: answer.value.startedAt ?? null, courseCode: answer.value.courseCode ?? null }
        return prev && prev.startedAt === next.startedAt && prev.courseCode === next.courseCode ? prev : next
      })
    } else {
      setLive(null)
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    if (fixture || !nativeRecorderAvailable()) return
    void probe()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void probe()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      mounted.current = false
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [probe, fixture])

  useEffect(() => {
    if (fixture || !live) return
    const timer = window.setInterval(() => void probe(), POLL_MS)
    return () => window.clearInterval(timer)
  }, [probe, live, fixture])

  return fixture ?? live
}

/**
 * SECONDS SINCE `startedAt`, ticking once a second while mounted. The recorder's own start time
 * against the wall clock — the same arithmetic the Recordings screen's live card does.
 */
export function useElapsed(startedAt: string | null): number | null {
  const started = startedAt ? Date.parse(startedAt) : NaN
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!Number.isFinite(started)) return
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [started])
  return Number.isFinite(started) ? Math.max(0, Math.round((now - started) / 1000)) : null
}

/**
 * DRAW `el` AT ITS BOARD WIDTH, SCALED TO FIT. The hero is a fixed composition (board 01: a 362px
 * card with a 152px corner cut out and a 184px column beside it); squeezed below that width the
 * column runs into the circle. So it keeps its proportions and is scaled with CSS `zoom` to the
 * width it has — at the owner's 130% size on a 360px phone that is about 0.69 of the board, which
 * on screen is about the board's own size. Never scaled up: wider than the board, it stretches.
 */
export function useFitWidth(ref: RefObject<HTMLElement | null>, designWidth: number): void {
  useEffect(() => {
    const el = ref.current
    const parent = el?.parentElement
    if (!el || !parent) return
    const fit = () => {
      // The parent's CONTENT box: clientWidth counts its padding (the screen's 14px gutters).
      const cs = getComputedStyle(parent)
      const w = parent.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0)
      if (w <= 0) return
      // Wider than the board: the card simply stretches (the column stays left, the circle right).
      if (w >= designWidth) {
        el.style.width = '100%'
        el.style.zoom = ''
      } else {
        el.style.width = `${designWidth}px`
        el.style.zoom = String(w / designWidth)
      }
    }
    fit()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(fit)
    ro.observe(parent)
    return () => ro.disconnect()
  }, [ref, designWidth])
}

/**
 * SHRINK ONE LINE OF TYPE UNTIL IT FITS ITS BOX. Board 01 sets the weekday at 58px, which fits
 * "Tuesday" beside the status chip and does not fit "Wednesday". The size steps down only as far
 * as the line needs, and never goes above `max`.
 */
export function useFitText(
  ref: RefObject<HTMLElement | null>,
  max: number,
  text: string,
  /** Where to publish the settled size as `--m-weekday-size`, for what aligns to it (the chip). */
  scope?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const el = ref.current
    const box = el?.parentElement
    if (!el || !box) return
    const fit = () => {
      el.style.fontSize = `${max}px`
      const natural = el.scrollWidth
      const room = box.clientWidth
      if (natural > 0 && room > 0 && natural > room) {
        el.style.fontSize = `${Math.floor(max * (room / natural) * 10) / 10}px`
      }
      scope?.current?.style.setProperty('--m-weekday-size', el.style.fontSize)
    }
    fit()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(fit)
    ro.observe(box)
    return () => ro.disconnect()
  }, [ref, max, text, scope])
}
